import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import { allCategories, allComics, comicsByCategory } from '@/mocks/comics';

export default function CategoryDetail() {
  const { slug } = useParams<{ slug: string }>();
  const category = allCategories.find((c) => c.id === slug);
  const comicIds = slug ? (comicsByCategory[slug] || []) : [];
  const comics = allComics.filter((c) => comicIds.includes(c.id));
  const [filter, setFilter] = useState<'all' | 'ongoing' | 'completed'>('all');

  const filteredComics = filter === 'all'
    ? comics
    : comics.filter((c) => filter === 'ongoing' ? c.status === '连载中' : c.status === '已完结');

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'http://emenculture.com/' },
          { '@type': 'ListItem', position: 2, name: '全部分类', item: 'http://emenculture.com/category' },
          { '@type': 'ListItem', position: 3, name: `${category?.name || slug}漫画`, item: 'http://emenculture.com/' },
        ],
      },
      {
        '@type': 'CollectionPage',
        name: `${category?.name || slug}漫画 - 韩漫网`,
        description: `韩漫网${category?.name || slug}分类，提供高清${category?.name || slug}韩国漫画免费在线阅读，每日更新`,
        url: 'http://emenculture.com/',
        isPartOf: { '@id': 'http://emenculture.com/#website' },
      },
    ],
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [slug]);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center px-6">
            <h1 className="font-heading text-4xl font-black text-foreground-900">分类不存在</h1>
            <Link to="/category" className="mt-4 inline-flex items-center gap-2 text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
              <i className="ri-arrow-left-line"></i> 返回分类列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main>
        <section className="relative pt-28 pb-14 md:pt-36 md:pb-16 bg-background-100">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to="/category" className="hover:text-primary-500 transition-colors cursor-pointer">全部分类</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-900 font-medium">{category.name}</span>
            </nav>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
              <div className="w-16 h-16 rounded-2xl bg-primary-100 flex items-center justify-center">
                <i className={`${category.icon} text-2xl text-primary-500`}></i>
              </div>
              <div className="flex-1">
                <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground-900">
                  {category.name}漫画
                </h1>
                <p className="mt-2 text-sm text-foreground-600">{category.description}</p>
              </div>
              <div className="text-sm text-foreground-500 whitespace-nowrap">
                共 <strong className="text-foreground-900">{category.comicCount}</strong> 部作品
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10">
            <div className="flex items-center gap-2 mb-8">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  filter === 'all' ? 'bg-foreground-900 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                全部
              </button>
              <button
                onClick={() => setFilter('ongoing')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  filter === 'ongoing' ? 'bg-foreground-900 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                连载中
              </button>
              <button
                onClick={() => setFilter('completed')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  filter === 'completed' ? 'bg-foreground-900 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                已完结
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {filteredComics.map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>

            {filteredComics.length === 0 && (
              <div className="text-center py-16">
                <p className="text-foreground-500">暂无符合条件的漫画</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}